import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, initials } from "@/lib/format";
import { ResetPasswordDialog, ChangePasswordDialog } from "@/components/PasswordDialog";
import { UserRound, Wallet, TrendingUp, ArrowLeft, KeyRound, Mail, Phone, Building2, Calendar } from "lucide-react";

export default function ManagerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get(`/managers/${id}/details`).then((r) => setD(r.data)); }, [id]);

  if (!d) return <div className="text-muted-foreground py-20 text-center">Loading manager…</div>;
  const m = d.manager;
  const isSelf = user.id === m.id;
  const canReset = user.role === "super_admin" || user.role === "admin";

  return (
    <div className="animate-fade-up space-y-6" data-testid="manager-details-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary row-hover"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="card-surface rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-gold/20 text-gold flex items-center justify-center font-semibold text-lg num">{initials(m.name)}</div>
            <div>
              <div className="font-display text-2xl font-bold">{m.name}</div>
              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><Building2 className="h-3.5 w-3.5" /> {m.branch_name}</div>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {m.email}</span>
                {m.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {m.phone}</span>}
                {m.created_at && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {String(m.created_at).slice(0, 10)}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${m.active ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>{m.active ? "Active" : "Inactive"}</span>
            {canReset && !isSelf && (
              <ResetPasswordDialog userId={m.id} userName={m.name}
                trigger={<button data-testid="manager-reset-password" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40 row-hover"><KeyRound className="h-3.5 w-3.5" /> Reset Password</button>} />
            )}
            {isSelf && (
              <ChangePasswordDialog trigger={<button data-testid="manager-change-password" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40 row-hover"><KeyRound className="h-3.5 w-3.5" /> Change Password</button>} />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <SumCard testid="md-clients" label="Clients Managed" value={d.client_count} icon={UserRound} onClick={() => navigate(`/clients?manager=${id}`)} />
        <SumCard testid="md-aum" label="Total Assets / AUM" value={formatINR(d.total_aum)} icon={Wallet} onClick={() => navigate(`/clients?manager=${id}`)} />
        <SumCard testid="md-business" label="Total Business" value={formatINR(d.total_business)} icon={TrendingUp} />
      </div>

      <div className="card-surface rounded-xl p-5">
        <h3 className="font-display font-semibold mb-4">Product-wise Business</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="md-product-grid">
          {d.product_business.map((p) => (
            <button key={p.key} onClick={() => navigate(`/clients?manager=${id}&product=${p.key}`)} data-testid={`md-product-${p.key}`} className="text-left rounded-lg border border-border p-4 row-hover hover:border-primary/40 hover:bg-secondary">
              <div className="text-sm font-medium">{p.label}</div>
              <div className="mt-1 font-display text-lg font-bold num text-primary">{formatINR(p.current)}</div>
              <div className="text-xs text-muted-foreground num">{p.clients} clients · {p.count} holdings</div>
            </button>
          ))}
          {d.product_business.length === 0 && <div className="text-sm text-muted-foreground">No holdings yet.</div>}
        </div>
      </div>
    </div>
  );
}

function SumCard({ label, value, icon: Icon, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid} className={`card-surface rounded-xl p-5 stat-glow ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div><div className="text-xs text-muted-foreground font-medium">{label}</div><div className="mt-2 font-display text-2xl font-bold num">{value}</div></div>
        <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );
}
